// Apolo IDE — Council SSE client (v2).
//
// Wraps fetch + ReadableStream + TextDecoder to read NDJSON-over-SSE events
// from POST /api/council/run and dispatch them to callback hooks.
//
// v2 (SPEC §events/§terminal):
//   • a caller that explicitly sets `protocolVersion: 2` sends the v2 body
//     (protocol_version + mode, default 'rebuttal'); a BARE caller keeps v1
//     semantics byte-for-byte — protocol versioning is opt-in;
//   • the phase travels as a named string in the SAME `phase` field (v2) or a
//     legacy number (v1); the client passes events through untouched;
//   • SETTLES EXACTLY ONCE. Network throw, HTTP error, missing body, mid-stream
//     read error and unexpected EOF all resolve to a single terminal callback,
//     preserving output already delivered. After terminal no further callback
//     fires, and the reader is stopped.
//   • EOF/read failure with NO received content is an ERROR; with content it is
//     PARTIAL (security-design H1).
//   • never echoes a raw HTTP body or upstream error string back to the caller.
//
// Returns synchronously with `{abort, promise}` so the caller can hook abort to
// a Cancel button immediately.

import { apiUrl } from './apiBase.js';
import { getStoredToken } from './api.js';
import { normalizeStatus, statusOnEof } from './councilProtocol.js';

export const MIN_AGENTS = 2;
// Teto do conselho = roster visível inteiro (era 6 e bloqueava seleções maiores).
export const MAX_AGENTS = 15;

/**
 * @typedef {Object} CouncilCallbacks
 * @property {(payload: object) => void} [onStart]
 * @property {(payload: {phase: number|string, agents?: string[], chairman?: string}) => void} [onPhaseStart]
 * @property {(payload: {agent_id: string, phase: number|string, chunk: string}) => void} [onChunk]
 * @property {(payload: {agent_id: string, phase: number|string, tokens_in: number|null, tokens_out: number|null}) => void} [onStreamDone]
 * @property {(payload: {phase: number|string, results?: any[], label_mapping?: object, result?: any}) => void} [onPhaseComplete]
 * @property {(payload: {code: string, agent_id?: string, phase?: number|string, message?: string}) => void} [onError]
 * @property {(payload: object) => void} [onDone]
 */

// Stable, allow-listed client-side error codes. Raw upstream/HTTP bodies are
// never forwarded to callbacks (security-design L1) — only these codes plus the
// HTTP status number, which is not sensitive.
const ERR_NETWORK = 'network';
const ERR_STREAM = 'stream_error';
const ERR_NO_BODY = 'no_body';

/**
 * @param {{query: string, agents: string[], chairman?: string, enableReview?: boolean,
 *          mode?: 'essential'|'rebuttal', protocolVersion?: 1|2}} input
 * @param {CouncilCallbacks} callbacks
 * @returns {{abort: () => void, promise: Promise<void>}}
 */
export function runCouncil(input, callbacks = {}) {
  const query = typeof input?.query === 'string' ? input.query.trim() : '';
  const agents = Array.isArray(input?.agents) ? input.agents.filter((a) => typeof a === 'string' && a) : [];
  const chairman = input?.chairman || agents[0];
  const enableReview = input?.enableReview !== false;
  // v2 is OPT-IN: a bare caller stays on v1. Only protocolVersion:2 selects the
  // v2 body (with mode, default rebuttal).
  const isV2 = input?.protocolVersion === 2;
  const mode = isV2 ? (input?.mode === 'essential' ? 'essential' : 'rebuttal') : undefined;

  if (!query) throw new Error('query must be a non-empty string');
  if (agents.length < MIN_AGENTS) throw new Error(`at least ${MIN_AGENTS} agents required`);
  if (agents.length > MAX_AGENTS) throw new Error(`at most ${MAX_AGENTS} agents allowed`);

  const controller = new AbortController();
  let settled = false;
  // Every callback is gated on the terminal guard so a late chunk/heartbeat after
  // settle is inert (no state writes after the run ended).
  const emit = (name, payload) => {
    if (settled) return;
    callbacks[name]?.(payload);
  };
  const settle = (event) => {
    if (settled) return;
    settled = true;
    callbacks.onDone?.(event);
  };

  const body = { query, agents, chairman, enable_review: enableReview };
  if (isV2) {
    body.protocol_version = 2;
    body.mode = mode;
  }

  const promise = (async () => {
    let res;
    try {
      res = await fetch(apiUrl('/api/council/run'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getStoredToken() || ''}`,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (e) {
      const aborted = e?.name === 'AbortError';
      if (!aborted) emit('onError', { code: ERR_NETWORK });
      settle({ status: aborted ? 'cancelled' : 'error', cancelled: aborted });
      return;
    }
    if (!res.ok) {
      // Status code only; never the response body.
      emit('onError', { code: `http_${res.status}` });
      settle({ status: 'error' });
      return;
    }
    if (!res.body) {
      emit('onError', { code: ERR_NO_BODY });
      settle({ status: 'error' });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let sawTerminal = false;
    let received = false;
    try {
      while (!sawTerminal) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 2);
          const outcome = dispatchDataBlock(block, emit, settle);
          if (outcome.content) received = true;
          if (outcome.terminal) { sawTerminal = true; break; }
        }
      }
      if (!sawTerminal && buffer.trim()) {
        const outcome = dispatchDataBlock(buffer, emit, settle);
        if (outcome.content) received = true;
        if (outcome.terminal) sawTerminal = true;
      }
    } catch (e) {
      const aborted = e?.name === 'AbortError';
      if (!aborted) emit('onError', { code: ERR_STREAM });
      // A read failure after we received output is partial, not a total loss.
      settle({ status: aborted ? 'cancelled' : statusOnEof(received), cancelled: aborted });
      return;
    } finally {
      // Stop the reader on terminal so no further network work happens.
      try { await reader.cancel(); } catch { /* already closed */ }
      try { reader.releaseLock(); } catch { /* ignore */ }
    }
    // Stream ended without a server terminal event: EOF. Content decides.
    if (!sawTerminal) settle({ status: statusOnEof(received), eof: true });
  })();

  return {
    abort: () => {
      controller.abort();
      // Settle immediately so a UI awaiting the promise never waits on the
      // runtime to surface the disconnect.
      settle({ status: 'cancelled', cancelled: true });
    },
    promise,
  };
}

/**
 * Parse a single `data:` block and dispatch it.
 * @returns {{content: boolean, terminal: boolean}}
 */
function dispatchDataBlock(block, emit, settle) {
  const line = block.trim();
  if (!line.startsWith('data:')) return { content: false, terminal: false };
  const payload = line.slice(5).trim();
  if (!payload) return { content: false, terminal: false };
  let event;
  try { event = JSON.parse(payload); } catch { return { content: false, terminal: false }; }
  return dispatch(event, emit, settle);
}

function dispatch(event, emit, settle) {
  if (!event || typeof event.type !== 'string') return { content: false, terminal: false };
  const { type, ...rest } = event;
  if (type === 'council:start') { emit('onStart', rest); return { content: false, terminal: false }; }
  if (type === 'phase:start') { emit('onPhaseStart', rest); return { content: false, terminal: false }; }
  if (type === 'stream:chunk') { emit('onChunk', rest); return { content: true, terminal: false }; }
  if (type === 'stream:done') { emit('onStreamDone', rest); return { content: true, terminal: false }; }
  if (type === 'phase:complete') { emit('onPhaseComplete', rest); return { content: true, terminal: false }; }
  if (type === 'error') { emit('onError', rest); return { content: false, terminal: false }; }
  if (type === 'council:done') {
    settle({ ...rest, status: normalizeStatus(rest.status) });
    return { content: false, terminal: true };
  }
  // heartbeat ignored
  return { content: false, terminal: false };
}
